import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LockClosedIcon, EnvelopeIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

// Components
import Logo from '../../components/Logo';
import OrganizationForm from "./OrganizationForm";

// Actions and utilities
import { registerUser, apiError } from "../../store/actions";
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';
import { handleErrors, showToaster } from "components/Common/common";
import { PAGEPATHS } from "components/Common/Constant";
import GoogleSignupComp from "./GoogleSignup";
import GitHubSignupComp from "./GitHubSignupComp";
import LinkedInSignupComp from "./LinkedInSignupComp";


const Register = () => {
  document.title = "Register | GOVCOM - Government Compliance Dashboard";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [suggestions, setSuggestions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [orgInputValue, setOrgInputValue] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [addOrgButton, setAddOrgButton] = useState(false);
  const [modal, setModal] = useState(false);
  const [isModifying, setIsModifying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      firstname: "",
      lastname: "",
      password: "",
      organization: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Please Enter Your Email"),
      firstname: Yup.string().required("Please Enter Your First Name"),
      lastname: Yup.string().required("Please Enter Your Last Name"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Please Enter Your Password"),
      organization: Yup.string().required("Please Enter Your Organization")
    }),
    onSubmit: values => {
      dispatch(registerUser(values));
      handleSubmit(1, values);
    },
  });

  const orgValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      branch: '',
      agency: '',
      office: undefined,
      city: undefined,
      state: undefined,
      securityContact: undefined
    },
    validationSchema: Yup.object({
      branch: Yup.string().required("Please Enter Your Branch"),
      agency: Yup.string().required("Please Enter Your Agency")
    }),
    onSubmit: values => {
      handleSubmit(2, values);
    },
  });

  const selectAccountState = state => state.Account;
  const AccountProperties = createSelector(selectAccountState, account => ({
    user: account.user,
    registrationError: account.registrationError,
    success: account.success
  }));

  const { success } = useSelector(AccountProperties);

  useEffect(() => {
    dispatch(apiError(""));
  }, [dispatch]);

  useEffect(() => {
    success && setTimeout(() => navigate(PAGEPATHS.Login), 2000);
  }, [success, navigate]);

  const handleSubmit = async (type, values) => {
    if (type === 1) {
      // Check if organization is selected
      if (!organizationId || organizationId === "") {
        // If user typed something but didn't select/create org, open modal
        if (orgInputValue && orgInputValue.trim().length > 0) {
          showToaster(0, "Please create the organization first before registering");
          setModal(true);
          return;
        }
        showToaster(0, "Please select or create an organization");
        return;
      }

      const postForm = {
        email: values.email,
        password: values.password,
        firstname: values.firstname,
        lastname: values.lastname,
        orgId: organizationId
      };

      setLoading(true);

      try {
        const { data } = await axiosInstance.post(ApiNames.USER_SIGNUP, postForm);
        if (data.status === 1) {
          showToaster(data.status, data.message);
          navigate(PAGEPATHS.Login, { state: { email: values.email, password: values.password } });
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setIsModifying(true);
        const { data } = await axiosInstance.post(ApiNames.ADD_ORGANIZATION, values);
        if (data.status) {
          setModal(false);
          setOrganizationId(data.data);
          showToaster(data.status, data.message);
          // Auto-submit registration after org creation
          setTimeout(() => {
            validation.handleSubmit();
          }, 500);
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsModifying(false);
      }
    }
  };

  const fetchSuggestions = async (query) => {
    if (!query) {
      setSuggestions([]);
      return;
    }
    try {
      const { data } = await axiosInstance.get(`${ApiNames.LIST_ORGANIZATION}?search=${query}`);
      setSuggestions(data.data);
      setDropdownOpen(data.data.length > 0);
      setAddOrgButton(query.length > 2 && !data.data.length > 0);
    } catch (error) {
      handleErrors(error);
    }
  };

  const handleOrgInputChange = e => {
    validation.handleChange(e);
    const value = e.target.value;
    setOrgInputValue(value);

    // Clear organizationId when user changes input
    // This ensures if they selected an org but then type a new name,
    // the old ID is cleared
    if (organizationId) {
      setOrganizationId("");
    }

    fetchSuggestions(value);
  };

  const handleSelect = org => {
    setOrgInputValue(org.branch);
    setOrganizationId(org._id);
    validation.setFieldValue("organization", org.branch);
    setSuggestions([]);
    setDropdownOpen(false);
    setAddOrgButton(false);
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 to-blue-700 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative z-10">
          <Link to="/" className="flex items-center space-x-3 text-white">
            {/* <Logo size={50} variant="white" checkColor="#3b82f6" />
            <span className="text-3xl font-bold">GOVCOM™</span> */}

            <img
              src="/images/Logo_high.png"
              alt="GOVCOM Logo"
              className="h-12 w-auto"
            />
          </Link>
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white mb-6">
            Join Government Agencies<br />Using GOVCOM
          </h1>
          <p className="text-blue-100 text-lg leading-relaxed max-w-md">
            Start scanning your government websites for compliance issues across multiple frameworks today.
          </p>

          {/* Free Trial Includes Section - Commented Out */}
          {/* <div className="mt-12 space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <h3 className="text-white font-semibold mb-2">Free Trial Includes:</h3>
              <ul className="space-y-2 text-blue-100">
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Unlimited compliance scans</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Access to all 18 frameworks</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>AI-powered analysis</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Detailed remediation guidance</span>
                </li>
              </ul>
            </div>
          </div> */}
        </div>

        <div className="relative z-10 text-blue-100 text-sm">
          © {new Date().getFullYear()} GOVCOM. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <Link to="/" className="lg:hidden flex items-center space-x-2 text-blue-600 mb-8">
              {/* <Logo size={42} />
              <span className="text-2xl font-bold">GOVCOM</span> */}
              <img
                src="/images/Logo_low.png"
                alt="GOVCOM Logo"
                className="h-10 w-auto"
              />
            </Link>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Create your account
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                Sign in instead
              </Link>
            </p>
          </div>

          <div className="mt-8">
            <form onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }} className="space-y-5">
              {/* First Name */}
              <div>
                <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="firstname"
                    name="firstname"
                    type="text"
                    value={validation.values.firstname || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`block w-full pl-10 pr-3 py-3 border ${validation.touched.firstname && validation.errors.firstname
                      ? 'border-red-300'
                      : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="John"
                  />
                </div>
                {validation.touched.firstname && validation.errors.firstname && (
                  <p className="mt-1 text-sm text-red-600">{validation.errors.firstname}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="lastname"
                    name="lastname"
                    type="text"
                    value={validation.values.lastname || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`block w-full pl-10 pr-3 py-3 border ${validation.touched.lastname && validation.errors.lastname
                      ? 'border-red-300'
                      : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="Doe"
                  />
                </div>
                {validation.touched.lastname && validation.errors.lastname && (
                  <p className="mt-1 text-sm text-red-600">{validation.errors.lastname}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={validation.values.email || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`block w-full pl-10 pr-3 py-3 border ${validation.touched.email && validation.errors.email
                      ? 'border-red-300'
                      : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="you@example.com"
                  />
                </div>
                {validation.touched.email && validation.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{validation.errors.email}</p>
                )}
              </div>

              {/* Organization */}
              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                  Organization <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="organization"
                    name="organization"
                    type="text"
                    value={orgInputValue}
                    onChange={handleOrgInputChange}
                    onBlur={validation.handleBlur}
                    className={`block w-full pl-10 pr-3 py-3 border ${validation.touched.organization && validation.errors.organization
                      ? 'border-red-300'
                      : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="Your Organization"
                  />
                  {/* Organization Dropdown */}
                  {dropdownOpen && suggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {suggestions.map((org, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleSelect(org)}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
                        >
                          {org.branch}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {validation.touched.organization && validation.errors.organization && (
                  <p className="mt-1 text-sm text-red-600">{validation.errors.organization}</p>
                )}
                {organizationId && (
                  <p className="mt-1 text-sm text-green-600 flex items-center">
                    <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Organization selected
                  </p>
                )}
                {addOrgButton && (
                  <button
                    type="button"
                    onClick={() => setModal(true)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-500"
                  >
                    + Add New Organization
                  </button>
                )}
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={validation.values.password || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`block w-full pl-10 pr-10 py-3 border ${validation.touched.password && validation.errors.password
                      ? 'border-red-300'
                      : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-600 hover:text-gray-800"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {validation.touched.password && validation.errors.password && (
                  <p className="mt-1 text-sm text-red-600">{validation.errors.password}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">At least 8 characters</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
              <GoogleSignupComp />
              <GitHubSignupComp />
              <LinkedInSignupComp />
            </form>
          </div>
        </div>
      </div>

      {/* Organization Modal */}
      {/* Organization Modal */}
      <OrganizationForm
        modal={modal}
        orgValidation={orgValidation}
        isModifying={isModifying}
        setModal={setModal}
      />
    </div>
  );
};

export default Register;
